'use client';

import { useEffect, useRef, useState } from 'react';

import { ChevronDown, ChevronUp, Check, Info, Plus, Trash2, Save, UsersRound } from 'lucide-react';

import { useDispatch } from 'react-redux';

import { createJob, updateJob } from '@/modules/jobs/store/jobThunk';

import { DEPARTMENTS } from "@/shared/constants/department";

import type {
  CreateJobPayload,
  EmploymentType,
  InterviewWorkflowRound,
  Job,
  JobStatus,
  JobVisibility,
  WorkplaceType,
} from '@/modules/jobs/types/job';

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/shared/components/ui/dialog';

import { Button } from '@/shared/components/ui/button';
import AlreadyAppliedDialog from '@/shared/components/dialogs/AlreadyAppliedDialog';
import { Input } from '@/shared/components/ui/input';
import { Label } from '@/shared/components/ui/label';
import { Textarea } from '@/shared/components/ui/textarea';

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/components/ui/select';

import { Separator } from '@/shared/components/ui/separator';
import { Switch } from '@/shared/components/ui/switch';

interface CreateJobDialogProps {
  open: boolean;

  onOpenChange: (open: boolean) => void;

  editingJob?: Job | null;
}

interface JobFormData {
  title: string;

  company: string;

  department: string;

  location: string;

  workplaceType: WorkplaceType;

  employmentType: EmploymentType;

  education: string;

  positions: number;

  minimumExperience: number | '';
  maximumExperience: number | '';

  minimumSalary: string;

  maximumSalary: string;

  applicationDeadline: string;

  description: string;

  aboutRole: string;

  responsibilities: string[];

  requirements: string[];

  skills: string[];

  benefits: string[];

  urgent: boolean;

  visibility: JobVisibility;

  status: JobStatus;
  interviewWorkflow: InterviewWorkflowRound[];

}

const DEFAULT_INTERVIEW_WORKFLOW: InterviewWorkflowRound[] = [
  { key: 'HR', name: 'HR Round', sequence: 1 },
  { key: 'TECHNICAL', name: 'Technical Round', sequence: 2 },
  { key: 'MANAGEMENT', name: 'Management Round', sequence: 3 },
];

const AVAILABLE_INTERVIEW_ROUNDS = [
  { key: 'HR', name: 'HR Round' },
  { key: 'TECHNICAL', name: 'Technical Round' },
  { key: 'MANAGEMENT', name: 'Management Round' },
];

const initialFormData: JobFormData = {
  title: '',

  company: '',

  department: '',

  location: '',

  workplaceType: 'ONSITE',

  employmentType: 'FULL_TIME',

  education: '',

  positions: 1,

  minimumExperience: 0,

  maximumExperience: 0,

  minimumSalary: '',

  maximumSalary: '',

  applicationDeadline: '',

  description: '',

  aboutRole: '',

  responsibilities: [''],

  requirements: [''],

  skills: [''],

  benefits: [''],


  urgent: false,

  visibility: 'PUBLIC',

  status: 'DRAFT',
  interviewWorkflow: DEFAULT_INTERVIEW_WORKFLOW.map((round) => ({ ...round })),
};

const companies = [
  'Dharohar Land Corp',
  'Realty Assistant',
  'Go Red',
  'Safe Earth Infra',
  'Vulcan Engineering',
  'Digibee Network',
];
const locations = ['Noida', 'Delhi', 'Gurgaon', 'Mumbai', 'Pune', 'Bangalore'];

const cleanList = (items: string[]) => {
  return items.map((item) => item.trim()).filter(Boolean);
};

export default function CreateJobDialog({ open, onOpenChange, editingJob }: CreateJobDialogProps) {
  const dispatch = useDispatch<any>();

  const [formData, setFormData] = useState<JobFormData>(initialFormData);



  const [isSubmitting, setIsSubmitting] = useState(false);
  const [customRoundName, setCustomRoundName] = useState('');
  const [isInterviewDropdownOpen, setIsInterviewDropdownOpen] = useState(false);
  const [selectContainer, setSelectContainer] = useState<HTMLElement | null>(null);
  const [showCustomRoundInput, setShowCustomRoundInput] = useState(false);
  const customRoundInputRef = useRef<HTMLInputElement>(null);

  const [submitError, setSubmitError] = useState<string | null>(null);
  const [deadlineError, setDeadlineError] = useState<string | null>(null);
  const deadlineInputRef = useRef<HTMLInputElement>(null);
  const [showSuccessPopup, setShowSuccessPopup] = useState(false);
  const [successPopup, setSuccessPopup] = useState({
    title: '',
    message: '',
  });

  useEffect(() => {
    if (!open) {
      return;
    }

    setSubmitError(null);
    setDeadlineError(null);
    if (!editingJob) {
      setFormData({
        ...initialFormData,
        interviewWorkflow: DEFAULT_INTERVIEW_WORKFLOW.map((round) => ({ ...round })),
      });
      setCustomRoundName('');
      setIsInterviewDropdownOpen(false);
      setShowCustomRoundInput(false);
      return;
    }

    const workflow = editingJob.interviewWorkflow?.length
      ? [...editingJob.interviewWorkflow]
        .sort((a, b) => a.sequence - b.sequence)
        .map((round, index) => ({ ...round, sequence: index + 1 }))
      : DEFAULT_INTERVIEW_WORKFLOW.map((round) => ({ ...round }));

    setFormData({
      title: editingJob.title ?? '',

      company: editingJob.company ?? '',

      department: editingJob.department ?? '',

      location: editingJob.location ?? '',

      workplaceType: editingJob.workplaceType,

      employmentType: editingJob.employmentType,

      education: editingJob.education ?? '',

      positions: editingJob.positions ?? 1,

      minimumExperience: editingJob.minimumExperience ?? 0,

      maximumExperience: editingJob.maximumExperience ?? 0,

      minimumSalary: editingJob.minimumSalary != null ? String(editingJob.minimumSalary) : '',

      maximumSalary: editingJob.maximumSalary != null ? String(editingJob.maximumSalary) : '',

      applicationDeadline: editingJob.applicationDeadline ?? '',

      description: editingJob.description ?? '',

      aboutRole: editingJob.aboutRole ?? '',

      responsibilities: editingJob.responsibilities?.length ? editingJob.responsibilities : [''],

      requirements: editingJob.requirements?.length ? editingJob.requirements : [''],

      skills: editingJob.skills?.length ? editingJob.skills : [''],

      benefits: editingJob.benefits?.length ? editingJob.benefits : [''],

      urgent: editingJob.priority === 'HIGH',

      visibility: editingJob.visibility,

      status: editingJob.status,
      interviewWorkflow: workflow,

    });
    setCustomRoundName('');
    setIsInterviewDropdownOpen(false);
    setShowCustomRoundInput(false);
  }, [open, editingJob]);

  const handleInput = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = event.target;

    setFormData((previousData) => ({
      ...previousData,
      [name]:
        event.target.type === 'number'
          ? value === ''
            ? ''
            : Number(value)
          : value,
    }));
  };

  const addItem = (field: 'responsibilities' | 'requirements' | 'skills' | 'benefits') => {
    setFormData((previousData) => ({
      ...previousData,

      [field]: [...previousData[field], ''],
    }));
  };

  const removeItem = (
    field: 'responsibilities' | 'requirements' | 'skills' | 'benefits',

    index: number
  ) => {
    setFormData((previousData) => ({
      ...previousData,

      [field]: previousData[field].filter((_, itemIndex) => itemIndex !== index),
    }));
  };

  const updateItem = (
    field: 'responsibilities' | 'requirements' | 'skills' | 'benefits',

    index: number,

    value: string
  ) => {
    setFormData((previousData) => {
      const updatedItems = [...previousData[field]];

      updatedItems[index] = value;

      return {
        ...previousData,

        [field]: updatedItems,
      };
    });
  };

  const toggleInterviewRound = (key: string, name: string) => {
    setFormData((previousData) => {
      const exists = previousData.interviewWorkflow.some((round) => round.key === key);

      if (exists) {
        const updatedWorkflow = previousData.interviewWorkflow
          .filter((round) => round.key !== key)
          .sort((a, b) => a.sequence - b.sequence)
          .map((round, index) => ({ ...round, sequence: index + 1 }));

        return {
          ...previousData,
          interviewWorkflow: updatedWorkflow,
        };
      }

      return {
        ...previousData,
        interviewWorkflow: [
          ...previousData.interviewWorkflow,
          {
            key,
            name,
            sequence: previousData.interviewWorkflow.length + 1,
          },
        ],
      };
    });
  };

  const addCustomInterviewRound = () => {
    const trimmedName = customRoundName.trim();

    if (!trimmedName) {
      customRoundInputRef.current?.focus();
      return;
    }

    const duplicateName = formData.interviewWorkflow.some(
      (round) => round.name.trim().toLowerCase() === trimmedName.toLowerCase()
    );

    if (duplicateName) {
      setSubmitError('Interview round name must be unique.');
      customRoundInputRef.current?.focus();
      return;
    }

    const customRound: InterviewWorkflowRound = {
      key: `CUSTOM_${Date.now()}`,
      name: trimmedName,
      sequence: formData.interviewWorkflow.length + 1,
    };

    setFormData((previousData) => ({
      ...previousData,
      interviewWorkflow: [...previousData.interviewWorkflow, customRound],
    }));

    setCustomRoundName('');
    setShowCustomRoundInput(false);
    setSubmitError(null);
  };

  const removeInterviewRound = (key: string) => {
    setFormData((previousData) => ({
      ...previousData,
      interviewWorkflow: previousData.interviewWorkflow
        .filter((round) => round.key !== key)
        .sort((a, b) => a.sequence - b.sequence)
        .map((round, index) => ({ ...round, sequence: index + 1 })),
    }));
  };

  const updateInterviewSequence = (key: string, sequence: number) => {
    setFormData((previousData) => ({
      ...previousData,
      interviewWorkflow: previousData.interviewWorkflow.map((round) =>
        round.key === key ? { ...round, sequence } : round
      ),
    }));
  };

  const validateInterviewWorkflow = () => {
    const workflow = formData.interviewWorkflow;

    if (!workflow.length) {
      return 'Please select at least one interview round.';
    }

    const normalizedNames = workflow.map((round) => round.name.trim().toLowerCase());

    if (new Set(normalizedNames).size !== normalizedNames.length) {
      return 'Interview round names must be unique.';
    }

    const sequences = workflow.map((round) => round.sequence);

    if (sequences.some((sequence) => !Number.isInteger(sequence) || sequence < 1)) {
      return 'Interview priority must be a positive whole number.';
    }

    if (new Set(sequences).size !== sequences.length) {
      return 'Duplicate interview priorities are not allowed.';
    }

    const sortedSequences = [...sequences].sort((a, b) => a - b);

    for (let index = 0; index < sortedSequences.length; index += 1) {
      if (sortedSequences[index] !== index + 1) {
        return `Interview priority must be continuous from 1 to ${workflow.length}.`;
      }
    }

    return null;
  };

  const buildPayload = (selectedStatus: JobStatus): CreateJobPayload => {
    return {
      title: formData.title.trim(),

      company: formData.company.trim() || null,

      department: formData.department,

      description: formData.description.trim(),

      aboutRole: formData.aboutRole.trim() || null,

      education: formData.education || null,

      employmentType: formData.employmentType,

      workplaceType: formData.workplaceType,

      location: formData.location,

      minimumExperience:
        formData.minimumExperience === ''
          ? 0
          : Number(formData.minimumExperience),

      maximumExperience:
        formData.maximumExperience === ''
          ? null
          : Number(formData.maximumExperience),

      minimumSalary: formData.minimumSalary ? Number(formData.minimumSalary) : null,

      maximumSalary: formData.maximumSalary ? Number(formData.maximumSalary) : null,

      currency: 'INR',

      positions: Number(formData.positions),
      priority: formData.urgent ? 'HIGH' : 'MEDIUM',

      status: selectedStatus,

      visibility: formData.visibility,

      responsibilities: cleanList(formData.responsibilities),

      requirements: cleanList(formData.requirements),

      skills: cleanList(formData.skills),

      benefits: cleanList(formData.benefits),

      applicationDeadline: formData.applicationDeadline || null,
      interviewWorkflow: [...formData.interviewWorkflow]

        .sort((a, b) => a.sequence - b.sequence)
        .map((round, index) => ({
          key: round.key.trim(),
          name: round.name.trim(),
          sequence: index + 1,
        })),
    };
  };

  const focusDeadlineField = () => {
    requestAnimationFrame(() => {
      deadlineInputRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });

      deadlineInputRef.current?.focus();
    });
  };

  const submitJob = async (selectedStatus: JobStatus) => {
    try {
      setIsSubmitting(true);
      setSubmitError(null);
      setDeadlineError(null);
      setCreatedJobId(null);

      const workflowError = validateInterviewWorkflow();

      if (workflowError) {
        setSubmitError(workflowError);
        setIsSubmitting(false);
        return;
      }

      // Frontend deadline validation
      if (formData.applicationDeadline) {
        const today = new Date();
        today.setHours(0, 0, 0, 0);

        const [year, month, day] = formData.applicationDeadline
          .split('-')
          .map(Number);

        const selectedDeadline = new Date(year, month - 1, day);
        selectedDeadline.setHours(0, 0, 0, 0);

        if (selectedDeadline < today) {
          setDeadlineError('Job deadline cannot be in the past.');
          setSubmitError(null);
          focusDeadlineField();
          return;
        }
      }
      const payload = buildPayload(selectedStatus);



      let isApprovalRequest = false;


      if (editingJob) {
        const updatedJob = await dispatch(
          updateJob({
            jobId: editingJob.id,
            payload,
          })
        ).unwrap();

        // Approval requests do not return a created/updated Job record.
        isApprovalRequest =
          updatedJob?.requestType === 'UPDATE' ||
          updatedJob?.type === 'APPROVAL_REQUEST' ||
          (!updatedJob?.jobCode && Boolean(updatedJob?.id));
      } else {
        const createdJob = await dispatch(
          createJob(payload)
        ).unwrap();

        isApprovalRequest =
          createdJob?.requestType === 'CREATE' ||
          createdJob?.type === 'APPROVAL_REQUEST' ||
          (!createdJob?.jobCode && Boolean(createdJob?.id));

        if (!isApprovalRequest) {
          setCreatedJobId(createdJob.id);
        }
      }


      const wasEditing = Boolean(editingJob);

      setFormData({
        ...initialFormData,
        interviewWorkflow: DEFAULT_INTERVIEW_WORKFLOW.map(
          (round) => ({
            ...round,
          })
        ),
      });

      setCustomRoundName("");
      setIsInterviewDropdownOpen(false);
      setShowCustomRoundInput(false);
      setSubmitError(null);
      setDeadlineError(null);
      setCreatedJobId(null);

      onOpenChange(false);


      setSuccessPopup({
        title: editingJob
          ? isApprovalRequest
            ? 'Job Changes Submitted for Approval'
            : 'Job Updated Successfully'
          : selectedStatus === 'DRAFT'
            ? 'Draft Saved Successfully'
            : isApprovalRequest
              ? 'Approval Request Submitted'
              : 'Job Published Successfully',

        message: editingJob
          ? isApprovalRequest
            ? 'Your proposed job changes have been submitted to HR for review. The current job remains unchanged until approval.'
            : 'The job details have been updated successfully.'
          : selectedStatus === 'DRAFT'
            ? 'Your job draft has been saved successfully.'
            : isApprovalRequest
              ? 'Your job has been submitted to HR for approval. It will be created after approval.'
              : 'Your job has been published successfully.',
      });

      setShowSuccessPopup(true);

    } catch (error: any) {
      console.log("JOB SAVE ERROR:", error);

      const message =
        error?.payload?.message ||
        error?.payload?.error?.message ||
        error?.response?.data?.message ||
        error?.response?.data?.error?.message ||
        error?.message ||
        (typeof error === "string" ? error : "");

      const errorMessage = String(message);

      if (
        errorMessage
          .toLowerCase()
          .includes("job deadline")
      ) {
        setDeadlineError(
          "Job deadline cannot be in the past."
        );
        setSubmitError(null);

        focusDeadlineField();

        return;
      }

      setSubmitError(
        errorMessage ||
        "Unable to save job. Please check the form and try again."
      );
    } finally {
      setIsSubmitting(false);
    }
  };
  const handleSubmit = () => {
    submitJob(formData.status);
  };

  const [createdJobId, setCreatedJobId] = useState<string | null>(null);

  const handleSaveDraft = () => {
    submitJob('DRAFT');
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent
          ref={setSelectContainer}
          className="max-h-[95vh] max-w-4xl overflow-y-auto"
        >
          <DialogHeader>
            <DialogTitle className="text-2xl">
              {editingJob ? 'Edit Job' : 'Create New Job'}
            </DialogTitle>

            <DialogDescription>Fill all required details.</DialogDescription>
          </DialogHeader>

          <div className="space-y-8 py-6">
            {/* BASIC INFORMATION */}

            <div className="rounded-xl border border-gray-200 p-6">
              <h2 className="mb-6 text-xl font-semibold">Basic Information</h2>
              <div className="grid gap-6">
                <div className="space-y-2">
                  <Label>Job Title *</Label>

                  <Input
                    name="title"
                    value={formData.title}
                    onChange={handleInput}
                    placeholder="Senior Sales Executive"
                  />
                </div>
              </div>


              <div className="mt-6 grid gap-6 md:grid-cols-2">
                <div className="space-y-2">
                  <Label>Company</Label>

                  <Select
                    value={formData.company}
                    onValueChange={(value) =>
                      setFormData((previousData) => ({
                        ...previousData,
                        company: value,
                      }))
                    }
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select Company" />
                    </SelectTrigger>
                    <SelectContent container={selectContainer}>
                      {companies.map((company) => (
                        <SelectItem
                          key={company}
                          value={company}
                        >
                          {company}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Department *</Label>

                  <Select
                    value={formData.department}
                    onValueChange={(value) =>
                      setFormData((previousData) => ({
                        ...previousData,

                        department: value,
                      }))
                    }
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select Department" />
                    </SelectTrigger>

                    <SelectContent container={selectContainer}>
                      {DEPARTMENTS.map((department) => (
                        <SelectItem key={department} value={department}>
                          {department}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="mt-6 grid gap-6 md:grid-cols-2">
                <div className="space-y-2">
                  <Label>Location *</Label>

                  <Select
                    value={formData.location}
                    onValueChange={(value) =>
                      setFormData((previousData) => ({
                        ...previousData,

                        location: value,
                      }))
                    }
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select Location" />
                    </SelectTrigger>

                    <SelectContent container={selectContainer}>
                      {locations.map((location) => (
                        <SelectItem key={location} value={location}>
                          {location}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Work Mode *</Label>

                  <Select
                    value={formData.workplaceType}
                    onValueChange={(value: WorkplaceType) =>
                      setFormData((previousData) => ({
                        ...previousData,

                        workplaceType: value,
                      }))
                    }
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>

                    <SelectContent container={selectContainer}>
                      <SelectItem value="ONSITE">Work From Office</SelectItem>

                      <SelectItem value="HYBRID">Hybrid</SelectItem>

                      <SelectItem value="REMOTE">Remote</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="mt-6 grid gap-6 md:grid-cols-3">
                <div className="space-y-2">
                  <Label>Employment Type</Label>

                  <Select
                    value={formData.employmentType}
                    onValueChange={(value: EmploymentType) =>
                      setFormData((previousData) => ({
                        ...previousData,

                        employmentType: value,
                      }))
                    }
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>

                    <SelectContent container={selectContainer}>
                      <SelectItem value="FULL_TIME">Full-time</SelectItem>

                      <SelectItem value="PART_TIME">Part-time</SelectItem>

                      <SelectItem value="CONTRACT">Contract</SelectItem>

                      <SelectItem value="INTERNSHIP">Internship</SelectItem>

                      <SelectItem value="FREELANCE">Freelance</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Education</Label>

                  <Select
                    value={formData.education}
                    onValueChange={(value) =>
                      setFormData((previousData) => ({
                        ...previousData,

                        education: value,
                      }))
                    }
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select Education" />
                    </SelectTrigger>

                    <SelectContent container={selectContainer}>
                      <SelectItem value="Graduate">Graduate</SelectItem>

                      <SelectItem value="B.Tech">B.Tech</SelectItem>

                      <SelectItem value="MBA">MBA</SelectItem>

                      <SelectItem value="MCA">MCA</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Open Positions</Label>

                  <Input
                    type="number"
                    min={1}
                    name="positions"
                    value={formData.positions}
                    onChange={handleInput}
                  />
                </div>
              </div>
            </div>

            {/* EXPERIENCE & SALARY */}

            <div className="rounded-xl border border-gray-200 p-6">
              <h2 className="mb-6 text-xl font-semibold">Experience & Salary</h2>

              <div className="grid gap-6 md:grid-cols-4">
                <div className="space-y-2">
                  <Label>Min Experience</Label>

                  <Input
                    type="number"
                    min={0}
                    name="minimumExperience"
                    value={formData.minimumExperience}
                    onFocus={(event) => {
                      if (event.currentTarget.value === '0') {
                        setFormData((prev) => ({
                          ...prev,
                          minimumExperience: '',
                        }));
                      }
                    }}
                    onChange={handleInput}
                  />
                </div>

                <div className="space-y-2">
                  <Label>Max Experience</Label>

                  <Input
                    type="number"
                    min={0}
                    name="maximumExperience"
                    value={formData.maximumExperience}
                    onFocus={(event) => {
                      if (event.currentTarget.value === '0') {
                        setFormData((prev) => ({
                          ...prev,
                          maximumExperience: '',
                        }));
                      }
                    }}
                    onChange={handleInput}
                  />
                </div>

                <div className="space-y-2">
                  <Label>Min Salary (LPA)</Label>

                  <Input
                    type="number"
                    min={0}
                    name="minimumSalary"
                    value={formData.minimumSalary}
                    onChange={handleInput}
                    placeholder="3"
                  />
                </div>

                <div className="space-y-2">
                  <Label>Max Salary (LPA)</Label>

                  <Input
                    type="number"
                    min={0}
                    name="maximumSalary"
                    value={formData.maximumSalary}
                    onChange={handleInput}
                    placeholder="8"
                  />
                </div>
              </div>

              <Separator className="my-8" />

              <div className="grid gap-6 md:grid-cols-2">
                <div className="space-y-2">
                  <Label>Application Deadline</Label>

                  <Input
                    ref={deadlineInputRef}
                    type="date"
                    name="applicationDeadline"
                    min={(() => {
                      const today = new Date();
                      const year = today.getFullYear();
                      const month = String(today.getMonth() + 1).padStart(2, '0');
                      const day = String(today.getDate()).padStart(2, '0');

                      return `${year}-${month}-${day}`;
                    })()}
                    value={formData.applicationDeadline}
                    onChange={(event) => {
                      const value = event.target.value;

                      setFormData((previousData) => ({
                        ...previousData,
                        applicationDeadline: value,
                      }));

                      setDeadlineError(null);
                      setSubmitError(null);
                    }}
                    className={deadlineError ? 'border-red-500' : ''}
                  />

                  {deadlineError && (
                    <p className="text-sm text-red-600">
                      {deadlineError}
                    </p>
                  )}
                </div>
              </div>
            </div>
            <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm sm:p-6">
              <div className="flex flex-col gap-5">
                <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                  <div className="flex items-start gap-3">
                    <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-slate-100 text-slate-700">
                      <UsersRound className="h-5 w-5" />
                    </div>

                    <div>
                      <h2 className="text-lg font-semibold tracking-tight text-slate-900 sm:text-xl">
                        Interview Process
                      </h2>
                      <p className="mt-1 text-sm leading-5 text-slate-500">
                        Select the interview rounds for this job and set their priority order.
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center gap-2 rounded-lg bg-slate-50 px-3 py-2 text-sm text-slate-600">
                    <Info className="h-4 w-4 shrink-0 text-slate-500" />
                    <span>Priority 1 will be the first round.</span>
                  </div>
                </div>

                <div className="relative">
                  <button
                    type="button"
                    onClick={() => setIsInterviewDropdownOpen((previous) => !previous)}
                    className="flex w-full items-center justify-between rounded-lg border border-slate-300 bg-white px-4 py-3 text-left transition hover:border-slate-400"
                  >
                    <div>
                      <p className="text-sm font-medium text-slate-900">Interview Rounds</p>
                      <p className="mt-0.5 text-xs text-slate-500">
                        {formData.interviewWorkflow.length}{' '}
                        {formData.interviewWorkflow.length === 1 ? 'round' : 'rounds'} selected
                      </p>
                    </div>

                    {isInterviewDropdownOpen ? (
                      <ChevronUp className="h-5 w-5 text-slate-500" />
                    ) : (
                      <ChevronDown className="h-5 w-5 text-slate-500" />
                    )}
                  </button>

                  {isInterviewDropdownOpen && (
                    <div className="relative mt-2 w-full rounded-lg border border-slate-200 bg-white shadow-lg">
                      <div className="max-h-[240px] overflow-y-auto overscroll-contain rounded-lg">
                        <div className="px-4 py-3">
                          <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                            Available Rounds
                          </p>
                        </div>

                        <div className="divide-y divide-slate-100">
                          {AVAILABLE_INTERVIEW_ROUNDS.map((round) => {
                            const selected = formData.interviewWorkflow.some(
                              (item) => item.key === round.key
                            );
                            const selectedRound = formData.interviewWorkflow.find(
                              (item) => item.key === round.key
                            );

                            return (
                              <div
                                key={round.key}
                                className="flex items-center justify-between gap-4 px-4 py-3"
                              >
                                <label className="flex min-w-0 flex-1 cursor-pointer items-center gap-3">
                                  <input
                                    type="checkbox"
                                    checked={selected}
                                    onChange={() => {
                                      toggleInterviewRound(round.key, round.name);
                                      setSubmitError(null);
                                    }}
                                    className="h-4 w-4 rounded border-slate-300"
                                  />
                                  <span className="truncate text-sm font-medium text-slate-800">
                                    {round.name}
                                  </span>
                                </label>

                                {selected && selectedRound && (
                                  <div className="flex shrink-0 items-center gap-2">
                                    <span className="text-xs font-medium text-slate-500">
                                      Priority
                                    </span>
                                    <Input
                                      type="number"
                                      min={1}
                                      max={formData.interviewWorkflow.length}
                                      value={selectedRound.sequence}
                                      onChange={(event) =>
                                        updateInterviewSequence(
                                          round.key,
                                          Number(event.target.value)
                                        )
                                      }
                                      className="h-8 w-20 text-center"
                                    />
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>

                        {formData.interviewWorkflow.some((round) =>
                          round.key.startsWith('CUSTOM_')
                        ) && (
                            <>
                              <div className="px-4 py-3">
                                <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                                  Custom Rounds
                                </p>
                              </div>

                              <div className="divide-y divide-slate-100 border-t border-slate-100">
                                {formData.interviewWorkflow
                                  .filter((round) => round.key.startsWith('CUSTOM_'))
                                  .map((round) => (
                                    <div
                                      key={round.key}
                                      className="flex items-center justify-between gap-4 px-4 py-3"
                                    >
                                      <label className="flex min-w-0 flex-1 cursor-pointer items-center gap-3">
                                        <input
                                          type="checkbox"
                                          checked
                                          onChange={() => {
                                            removeInterviewRound(round.key);
                                            setSubmitError(null);
                                          }}
                                          className="h-4 w-4 rounded border-slate-300"
                                        />
                                        <span className="truncate text-sm font-medium text-slate-800">
                                          {round.name}
                                        </span>
                                      </label>

                                      <div className="flex shrink-0 items-center gap-2">
                                        <span className="text-xs font-medium text-slate-500">
                                          Priority
                                        </span>
                                        <Input
                                          type="number"
                                          min={1}
                                          max={formData.interviewWorkflow.length}
                                          value={round.sequence}
                                          onChange={(event) =>
                                            updateInterviewSequence(
                                              round.key,
                                              Number(event.target.value)
                                            )
                                          }
                                          className="h-8 w-20 text-center"
                                        />
                                        <Button
                                          type="button"
                                          variant="ghost"
                                          size="icon"
                                          onClick={() => {
                                            removeInterviewRound(round.key);
                                            setSubmitError(null);
                                          }}
                                          className="h-8 w-8 text-slate-400 hover:text-red-600"
                                        >
                                          <Trash2 className="h-4 w-4" />
                                        </Button>
                                      </div>
                                    </div>
                                  ))}
                              </div>
                            </>
                          )}

                        <div className="border-t border-slate-200 p-4">
                          {!showCustomRoundInput ? (
                            <Button
                              type="button"
                              variant="outline"
                              className="w-full"
                              onClick={() => {
                                setShowCustomRoundInput(true);
                                setSubmitError(null);
                              }}
                            >
                              <Plus className="mr-2 h-4 w-4" />
                              Add Custom Round
                            </Button>
                          ) : (
                            <div className="flex items-center gap-2">
                              <Input
                                ref={customRoundInputRef}
                                value={customRoundName}
                                onChange={(event) => {
                                  setCustomRoundName(event.target.value);
                                  setSubmitError(null);
                                }}
                                onKeyDown={(event) => {
                                  if (event.key === 'Enter') {
                                    event.preventDefault();
                                    addCustomInterviewRound();
                                  }

                                  if (event.key === 'Escape') {
                                    setCustomRoundName('');
                                    setShowCustomRoundInput(false);
                                  }
                                }}
                                placeholder="Enter custom round name"
                                autoFocus
                              />

                              <Button
                                type="button"
                                onClick={addCustomInterviewRound}
                              >
                                <Plus className="mr-2 h-4 w-4" />
                                Add
                              </Button>

                              <Button
                                type="button"
                                variant="ghost"
                                onClick={() => {
                                  setCustomRoundName('');
                                  setShowCustomRoundInput(false);
                                }}
                              >
                                Cancel
                              </Button>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {formData.interviewWorkflow.length > 0 && (
                  <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
                    <div className="mb-3 flex items-center gap-2">
                      <Check className="h-4 w-4 text-green-600" />
                      <p className="text-sm font-semibold text-slate-800">
                        Interview Sequence
                      </p>
                    </div>

                    <div className="flex flex-wrap items-center gap-2">
                      {[...formData.interviewWorkflow]
                        .sort((a, b) => a.sequence - b.sequence)
                        .map((round, index) => (
                          <div key={round.key} className="flex items-center gap-2">
                            <div className="flex items-center gap-2 rounded-full border border-slate-200 bg-white px-3 py-1.5">
                              <span className="flex h-5 w-5 items-center justify-center rounded-full bg-slate-900 text-[10px] font-semibold text-white">
                                {index + 1}
                              </span>
                              <span className="text-xs font-medium text-slate-700">
                                {round.name}
                              </span>
                            </div>

                            {index < formData.interviewWorkflow.length - 1 && (
                              <span className="text-slate-400">→</span>
                            )}
                          </div>
                        ))}
                    </div>
                  </div>
                )}


              </div>
            </div>

            {/* JOB DESCRIPTION */}

            <div className="rounded-xl border border-gray-200 p-6">
              <h2 className="mb-6 text-xl font-semibold">Job Description</h2>

              <div className="space-y-2">
                <Label>Job Description *</Label>

                <Textarea
                  name="description"
                  value={formData.description}
                  onChange={handleInput}
                  placeholder="Enter a detailed description of the job role..."
                  rows={6}
                />

                <p className="text-xs text-muted-foreground">Minimum 30 characters are required.</p>
              </div>

              <div className="mt-6 space-y-2">
                <Label>About the Role</Label>

                <Textarea
                  name="aboutRole"
                  value={formData.aboutRole}
                  onChange={handleInput}
                  placeholder="Explain the role, team and expected impact..."
                  rows={5}
                />
              </div>
            </div>

            {/* RESPONSIBILITIES */}

            <div className="rounded-xl border border-gray-200 p-6">
              <div className="mb-6 flex items-center justify-between gap-4">
                <div>
                  <h2 className="text-xl font-semibold">Responsibilities</h2>

                  <p className="mt-1 text-sm text-muted-foreground">
                    Add the primary responsibilities of this role.
                  </p>
                </div>

                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => addItem('responsibilities')}
                >
                  <Plus className="mr-2 h-4 w-4" />
                  Add
                </Button>
              </div>

              <div className="space-y-3">
                {formData.responsibilities.map((responsibility, index) => (
                  <div key={`responsibility-${index}`} className="flex items-center gap-3">
                    <Input
                      value={responsibility}
                      onChange={(event) => updateItem('responsibilities', index, event.target.value)}
                      placeholder={`Responsibility ${index + 1}`}
                    />

                    <Button
                      type="button"
                      variant="outline"
                      size="icon"
                      disabled={formData.responsibilities.length === 1}
                      onClick={() => removeItem('responsibilities', index)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
              </div>
            </div>

            {/* REQUIREMENTS */}

            <div className="rounded-xl border border-gray-200 p-6">
              <div className="mb-6 flex items-center justify-between gap-4">
                <div>
                  <h2 className="text-xl font-semibold">Requirements</h2>

                  <p className="mt-1 text-sm text-muted-foreground">
                    Add the required qualifications and experience.
                  </p>
                </div>

                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => addItem('requirements')}
                >
                  <Plus className="mr-2 h-4 w-4" />
                  Add
                </Button>
              </div>

              <div className="space-y-3">
                {formData.requirements.map((requirement, index) => (
                  <div key={`requirement-${index}`} className="flex items-center gap-3">
                    <Input
                      value={requirement}
                      onChange={(event) => updateItem('requirements', index, event.target.value)}
                      placeholder={`Requirement ${index + 1}`}
                    />

                    <Button
                      type="button"
                      variant="outline"
                      size="icon"
                      disabled={formData.requirements.length === 1}
                      onClick={() => removeItem('requirements', index)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
              </div>
            </div>

            {/* SKILLS */}

            <div className="rounded-xl border border-gray-200 p-6">
              <div className="mb-6 flex items-center justify-between gap-4">
                <div>
                  <h2 className="text-xl font-semibold">Required Skills</h2>

                  <p className="mt-1 text-sm text-muted-foreground">Add one skill in each field.</p>
                </div>

                <Button type="button" variant="outline" size="sm" onClick={() => addItem('skills')}>
                  <Plus className="mr-2 h-4 w-4" />
                  Add
                </Button>
              </div>

              <div className="space-y-3">
                {formData.skills.map((skill, index) => (
                  <div key={`skill-${index}`} className="flex items-center gap-3">
                    <Input
                      value={skill}
                      onChange={(event) => updateItem('skills', index, event.target.value)}
                      placeholder={`Skill ${index + 1}`}
                    />

                    <Button
                      type="button"
                      variant="outline"
                      size="icon"
                      disabled={formData.skills.length === 1}
                      onClick={() => removeItem('skills', index)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
              </div>
            </div>

            {/* BENEFITS */}

            <div className="rounded-xl border border-gray-200 p-6">
              <div className="mb-6 flex items-center justify-between gap-4">
                <div>
                  <h2 className="text-xl font-semibold">Benefits</h2>

                  <p className="mt-1 text-sm text-muted-foreground">Benefits are optional.</p>
                </div>

                <Button type="button" variant="outline" size="sm" onClick={() => addItem('benefits')}>
                  <Plus className="mr-2 h-4 w-4" />
                  Add
                </Button>
              </div>

              <div className="space-y-3">
                {formData.benefits.map((benefit, index) => (
                  <div key={`benefit-${index}`} className="flex items-center gap-3">
                    <Input
                      value={benefit}
                      onChange={(event) => updateItem('benefits', index, event.target.value)}
                      placeholder={`Benefit ${index + 1}`}
                    />

                    <Button
                      type="button"
                      variant="outline"
                      size="icon"
                      disabled={formData.benefits.length === 1}
                      onClick={() => removeItem('benefits', index)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
              </div>
            </div>

            {/* PUBLISH SETTINGS */}

            <div className="rounded-xl border border-gray-200 p-6">
              <h2 className="mb-6 text-xl font-semibold">Publish Settings</h2>

              <div className="flex items-center justify-between gap-6">
                <div>
                  <Label>Urgent Hiring</Label>

                  <p className="mt-1 text-sm text-muted-foreground">
                    Urgent jobs will be saved with high priority.
                  </p>
                </div>

                <Switch
                  checked={formData.urgent}
                  onCheckedChange={(checked) =>
                    setFormData((previousData) => ({
                      ...previousData,
                      urgent: checked,
                    }))
                  }
                />
              </div>

              <Separator className="my-6" />

              <div className="grid gap-6 md:grid-cols-2">
                <div className="space-y-2">
                  <Label>Job Visibility *</Label>

                  <Select
                    value={formData.visibility}
                    onValueChange={(value: JobVisibility) =>
                      setFormData((previousData) => ({
                        ...previousData,
                        visibility: value,
                      }))
                    }
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>

                    <SelectContent container={selectContainer}>
                      <SelectItem value="PUBLIC">Public</SelectItem>

                      <SelectItem value="INTERNAL">Internal</SelectItem>

                    </SelectContent>
                  </Select>

                  <p className="text-xs text-muted-foreground">
                    Only Published + Public jobs appear on the Career Page.
                  </p>
                </div>

                <div className="space-y-2">
                  <Label>Job Status *</Label>

                  <Select
                    value={formData.status}
                    onValueChange={(value: JobStatus) =>
                      setFormData((previousData) => ({
                        ...previousData,
                        status: value,
                      }))
                    }
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>

                    <SelectContent container={selectContainer}>
                      <SelectItem value="DRAFT">Draft</SelectItem>

                      <SelectItem value="PUBLISHED">Published</SelectItem>

                      <SelectItem value="CLOSED">Closed</SelectItem>

                      <SelectItem value="ARCHIVED">Archived</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            {createdJobId && (
              <div className="rounded-lg border border-green-200 bg-green-50 px-4 py-3">
                <p className="text-sm font-semibold text-green-800">
                  Job posted successfully.
                </p>

                <p className="mt-1 text-sm text-green-700">
                  Job ID:{' '}
                  <span className="font-bold">
                    {createdJobId}
                  </span>
                </p>
              </div>
            )}

            {/* API ERROR */}

            {submitError && (
              <div
                role="alert"
                className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700"
              >
                {submitError}
              </div>
            )}
          </div>

          <DialogFooter className="gap-3">
            <Button
              type="button"
              variant="outline"
              disabled={isSubmitting}
              onClick={() => onOpenChange(false)}
            >
              Cancel
            </Button>

            <Button type="button" variant="outline" disabled={isSubmitting} onClick={handleSaveDraft}>
              <Save className="mr-2 h-4 w-4" />

              {isSubmitting ? 'Saving...' : 'Save as Draft'}
            </Button>

            <Button type="button" disabled={isSubmitting} onClick={handleSubmit}>
              {isSubmitting
                ? editingJob
                  ? 'Updating...'
                  : 'Creating...'
                : editingJob
                  ? 'Update Job'
                  : formData.status === 'PUBLISHED'
                    ? 'Publish Job'
                    : 'Create Job'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlreadyAppliedDialog
        open={showSuccessPopup}
        type="deleteSuccess"
        title={successPopup.title}
        message={successPopup.message}
        buttonText="Done"
        onOpenChange={setShowSuccessPopup}
        onViewApplicationStatus={() => setShowSuccessPopup(false)}
      />
    </>
  );
}
